import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createBlock as _createBlock, createVNode as _createVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "admin-dashboard" }
const _hoisted_2 = { class: "container-fluid dashboard-content" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-12" }
const _hoisted_5 = { class: "card" }
const _hoisted_6 = { class: "card-header" }
const _hoisted_7 = { class: "d-flex justify-content-between align-items-center" }
const _hoisted_8 = { class: "results-info" }
const _hoisted_9 = {
  key: 0,
  class: "skeleton skeleton-results-info"
}
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "text-danger" }
const _hoisted_12 = { key: 2 }
const _hoisted_13 = { class: "text-muted" }
const _hoisted_14 = { class: "filters-section" }
const _hoisted_15 = { class: "card-body" }

import { ref, onMounted, watch, computed } from "vue";
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { subDays } from "date-fns";
import { searchLogs } from "../../../api";

// Simple debounce utility
import LogFilters from "./components/LogFilters.vue";
import LogsTable from "./components/LogsTable.vue";


export default {
  __name: 'LogsDashboard',
  props: {
	adminApiUrl: {
		type: String,
		required: true,
	},
	userApiUrl: {
		type: String,
		required: true,
	},
},
  setup(__props) {

function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}
const props = __props;

const logsTableRef = ref(null);
const tableReady = ref(false);

// Get query client for manual operations
const queryClient = useQueryClient();

// Current search filters
const currentFilters = ref({});

// Use TanStack Query for logs search with reactive filters
const { data: logsData, isPending: loading, isError, error } = useQuery({
	queryKey: computed(() => ['admin', 'logs', currentFilters.value]),
	queryFn: ({ signal }) => {
		if (signal?.aborted) {
			throw new Error('Query was cancelled');
		}
		return searchLogs(currentFilters.value);
	},
	enabled: computed(() => Object.keys(currentFilters.value).length > 0),
	staleTime: 1000 * 60 * 2, // 2 minutes
	gcTime: 1000 * 60 * 10, // 10 minutes
	retry: 2,
	retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000),
});

// Extract data from query result
const logs = computed(() => logsData.value?.list || []);
const maxCount = computed(() => logsData.value?.maxCount || null);

// Helper to determine if we should show loading state
const isQueryActive = computed(() => Object.keys(currentFilters.value).length > 0);
const shouldShowLoading = computed(() => loading.value && isQueryActive.value);

// Create debounced search function for TanStack column filters
const debouncedColumnFilterSearch = debounce((columnFilters) => {
	// Convert TanStack column filters to our API format
	const apiFilters = {};

	columnFilters.forEach((filter) => {
		switch (filter.id) {
			case "logFrom":
				apiFilters.logFrom = filter.value
					? new Date(filter.value).toISOString()
					: null;
				break;
			case "logUntil":
				apiFilters.logUntil = filter.value
					? new Date(filter.value).toISOString()
					: null;
				break;
			case "action":
				apiFilters.action = filter.value || null;
				break;
			case "bookingKind":
				apiFilters.bookingKind = filter.value || null;
				break;
			case "roomId":
				apiFilters.roomId = filter.value || null;
				break;
			case "userName":
				apiFilters.userName = filter.value || null;
				break;
		}
	});

	// Update reactive filters - this will automatically trigger the query
	currentFilters.value = apiFilters;
}, 100);

const handleColumnFiltersChange = (columnFilters) => {
	debouncedColumnFilterSearch(columnFilters);
};

const handleReset = () => {
	if (logsTableRef.value?.table) {
		// Set default date range to last 7 days using TanStack filters
		const defaultFilters = [
			{ id: "logFrom", value: subDays(new Date(), 7) },
			{ id: "logUntil", value: new Date() },
		];

		logsTableRef.value.table.setColumnFilters(defaultFilters);
	}
};

// Watch for table to become available
watch(
	() => logsTableRef.value?.table,
	(table) => {
		if (table) {
			tableReady.value = true;

			// Set default date range to last 7 days using TanStack filters
			const defaultFilters = [
				{ id: "logFrom", value: subDays(new Date(), 7) },
				{ id: "logUntil", value: new Date() },
			];

			table.setColumnFilters(defaultFilters);
		}
	},
	{ immediate: true }
);

onMounted(() => {
	// Component mounted
});

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[1] || (_cache[1] = _createStaticVNode("<div class=\"admin-header\"><div class=\"container-fluid\"><div class=\"row align-items-center\"><div class=\"col\"><h1 class=\"admin-title\"> Admin Panel - Raumbuchungen </h1></div></div></div></div>", 1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", null, [
                  _cache[0] || (_cache[0] = _createElementVNode("h5", { class: "card-title mb-1" }, " Buchungsprotokoll ", -1 /* CACHED */)),
                  _createElementVNode("div", _hoisted_8, [
                    (shouldShowLoading.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9))
                      : (_unref(isError) && isQueryActive.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createElementVNode("span", _hoisted_11, " Fehler beim Laden der Logs: " + _toDisplayString(_unref(error)?.message || _unref(error)), 1 /* TEXT */)
                          ]))
                        : (logs.value.length > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              _createElementVNode("span", _hoisted_13, _toDisplayString(logs.value.length) + " Einträge gefunden ", 1 /* TEXT */)
                            ]))
                          : _createCommentVNode("v-if", true)
                  ])
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              (tableReady.value && logsTableRef.value?.table)
                ? (_openBlock(), _createBlock(LogFilters, {
                    key: 0,
                    table: logsTableRef.value.table,
                    loading: _unref(loading),
                    "user-api-url": __props.userApiUrl,
                    onReset: handleReset
                  }, null, 8 /* PROPS */, ["table", "loading", "user-api-url"]))
                : _createCommentVNode("v-if", true)
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createVNode(LogsTable, {
                ref_key: "logsTableRef",
                ref: logsTableRef,
                "max-count": maxCount.value,
                logs: logs.value,
                loading: shouldShowLoading.value,
                error: _unref(isError) && isQueryActive.value ? (_unref(error)?.message || _unref(error)) : '',
                onColumnFiltersChange: handleColumnFiltersChange
              }, null, 8 /* PROPS */, ["max-count", "logs", "loading", "error"])
            ])
          ])
        ])
      ])
    ])
  ]))
}
}

}